import React, { useEffect, useMemo, useState } from "react";
import { useParams, useOutletContext, Link } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

export default function Products() {
  const { categorySlug } = useParams();
  const { settings } = useOutletContext() || {};
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [activeCat, setActiveCat] = useState(categorySlug || "all");

  useEffect(() => { setActiveCat(categorySlug || "all"); }, [categorySlug]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { sort };
    if (activeCat && activeCat !== "all") params.category = activeCat;
    if (search) params.search = search;
    api.get("/products", { params }).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [activeCat, sort, search]);

  const currency = settings?.currency_symbol || "₺";
  const heading = useMemo(() => {
    if (activeCat === "all") return "Tüm Ürünler";
    const c = categories.find((x) => x.slug === activeCat);
    return c?.name || "Ürünler";
  }, [activeCat, categories]);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24" data-testid="products-page">
      <div className="text-center max-w-2xl mx-auto">
        <div className="text-xs tracking-[0.25em] uppercase text-larc-gold">— Koleksiyon</div>
        <h1 className="font-heading text-5xl md:text-6xl tracking-tight mt-3">{heading}</h1>
        <p className="text-larc-muted mt-4">Atölyemizde küçük partilerle dökülen, sevgiyle hazırlanmış el yapımı parçalar.</p>
      </div>

      <div className="mt-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div className="flex flex-wrap items-center gap-2">
          <CatBtn slug="all" label="Tümü" active={activeCat === "all"} to="/urunler" />
          {categories.map((c) => (
            <CatBtn key={c.slug} slug={c.slug} label={c.name} active={activeCat === c.slug} to={`/urunler/${c.slug}`} />
          ))}
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-larc-muted" />
            <input
              data-testid="product-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Ürün ara…"
              className="pl-10 pr-4 py-2.5 text-sm bg-white border border-larc-border rounded-full outline-none focus:border-larc-gold w-56"
            />
          </div>
          <select
            data-testid="product-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="text-sm bg-white border border-larc-border rounded-full px-4 py-2.5 outline-none focus:border-larc-gold"
          >
            <option value="newest">Yeni eklenenler</option>
            <option value="price_asc">Fiyat: Artan</option>
            <option value="price_desc">Fiyat: Azalan</option>
          </select>
        </div>
      </div>

      <div className="mt-14">
        {loading ? (
          <div className="text-center text-larc-muted py-20">Yükleniyor…</div>
        ) : products.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-products">
            <div className="font-heading text-3xl">Henüz ürün yok</div>
            <p className="text-larc-muted mt-3">Bu kategori için ürün bulunamadı. Lütfen başka bir kategori seçin.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-testid="products-grid">
            {products.map((p) => <ProductCard key={p.id} product={p} currency={currency} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function CatBtn({ slug, label, active, to }) {
  return (
    <Link
      to={to}
      data-testid={`cat-${slug}`}
      className={`text-sm px-5 py-2 rounded-full border transition-colors ${active ? "bg-larc-ink text-white border-larc-ink" : "bg-white border-larc-border text-larc-ink hover:border-larc-gold"}`}
    >
      {label}
    </Link>
  );
}
